import { Filter } from '~commonInfra/utils/Filter';
import {
  EditableConnection,
  IOAuthConnectionEditModel,
  PasswordUnchanged
} from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IGetCommonUserOAuthCredentialResult } from '~src/connectivity/serverApi/requests/OAuth/GetCommonUserOAuthCredentialRequest';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';

// W-22483245: sentinel credential id meaning "Add new OAuth credential" in the account dropdown.
// Shared so the OAuth dialog and the WIF-eligible dialog use the same value.
export const ADD_NEW_OAUTH_CREDENTIAL_OPTION_ID = '__ADD_NEW_OAUTH_CREDENTIAL';

// W-22483245: the OAuth credential model plus the account list needed to render the account picker.
// Both the stock OAuth dialog and the unified WIF-eligible dialog build the picker from this, so the
// derivation lives here rather than being duplicated per tree.
export interface IOAuthEditModelResult {
  model: IOAuthConnectionEditModel;
  credentials: IOAuthCredential[];
  addNewOAuthCredentialOptionValue: string | undefined;
}

export namespace OAuthEditModelLoader {

  export async function load(connections: EditableConnection[]): Promise<IOAuthEditModelResult> {
    const firstConnectionType = connections[0].connectionType;
    const firstConnectionDetails = connections[0].connectionDetails;

    let instanceUrl: string;
    let role: string;
    let oauthConfigId: string;
    let authType: string;
    let server: string;
    if (firstConnectionDetails.type === 'database') {
      instanceUrl = firstConnectionDetails.instanceUrl;
      // A snowflake connection with a default role stores role='', which must be matched exactly.
      role = firstConnectionType === 'snowflake' ? (firstConnectionDetails.role ?? '') : firstConnectionDetails.role;
      oauthConfigId = firstConnectionDetails.oauthConfigId;
      authType = firstConnectionDetails.authenticationType;
      server = firstConnectionDetails.serverName;
    }

    const oAuthCredentialTypeInfo: IOAuthCredentialTypeInfo = OAuthCredentialsService.getTypeInfo(firstConnectionType);
    const model: IOAuthConnectionEditModel = await createOAuthEditModel(connections);

    let credentialsQuery: ResourceQuery<IOAuthCredential> =
      OAuthCredentials.allValid().addFilter(Filter.fieldEq('type', firstConnectionType));

    if (instanceUrl)
      credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('instanceUrl', instanceUrl));
    if (role != null)
      credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('role', role));
    if (oauthConfigId)
      credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('oauthConfigId', oauthConfigId));

    if (firstConnectionType === 'snowflake' && authType === 'auth-keypair') {
      credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('authentication', authType));
      if (server)
        credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('server', server));
    }

    const result = await SliceableHelper.fetchAll(credentialsQuery);

    return {
      model,
      credentials: result.items,
      addNewOAuthCredentialOptionValue:
        OAuthCredentials.canAddForConnectionType(oAuthCredentialTypeInfo) ? ADD_NEW_OAUTH_CREDENTIAL_OPTION_ID : undefined
    };
  }

  async function createOAuthEditModel(
    connections: EditableConnection[]
  ): Promise<IOAuthConnectionEditModel> {
    const editModel: IOAuthConnectionEditModel = {
      credentialOption: 'mixed',
      credentialId: undefined,
      credentialNoValueOptionText: undefined,
      usingManagedKeychain: undefined,
      usingRemoteQueryAgent: undefined,
      usernamePassword: {
        username: '',
        password: { isEmbedded: false, newPassword: '' }
      }
    };

    if (connections.length > 0) {
      editModel.usingRemoteQueryAgent = connections[0].usingRemoteQueryAgent;
    }

    const result = await OAuthCredentials.commonCredentialForDatasources(connections.map(c => c.id)) as IGetCommonUserOAuthCredentialResult;
    let numAuthenticationTypes = 0;

    // OAuth and KeyPair credentials are also considered "embedded" so don't double-count
    if (result.hasEmbeddedPassword && !result.hasOAuthCredential && !result.hasKeyPairCredential) {
      numAuthenticationTypes++;
    }
    if (result.hasManagedKeychain) {
      numAuthenticationTypes++;
      editModel.usingManagedKeychain = true;
    }
    // OAuth and Keypair are separate authentication types - if both are true, it's mixed
    if (result.hasOAuthCredential) {
      numAuthenticationTypes++;
    }
    if (result.hasKeyPairCredential) {
      numAuthenticationTypes++;
    }

    // If all connections share one authentication type, select that option and fill in the details;
    // otherwise fall back to NONE to make clear the data source has no single valid credential set.
    if (numAuthenticationTypes !== 1) {
      editModel.credentialOption = 'none';
    } else {
      // Check OAuth/KeyPair first since they also set hasEmbeddedPassword to true
      if (result.hasKeyPairCredential) {
        editModel.credentialOption = 'keypair';
        if (result.oauthCredentialId) {
          editModel.credentialId = result.oauthCredentialId;
        }
      } else if (result.hasManagedKeychain || result.hasOAuthCredential) {
        editModel.credentialOption = 'oauth';
        if (result.oauthCredentialId) {
          editModel.credentialId = result.oauthCredentialId;
        } else {
          // The user can see the credential but does not own it: show it as an option with no value.
          editModel.credentialNoValueOptionText = result.commonUsername;
        }
      } else if (result.hasEmbeddedPassword) {
        editModel.credentialOption = 'password';
        editModel.usernamePassword = {
          username: result.commonUsername,
          password: { isEmbedded: true, newPassword: PasswordUnchanged }
        };
      }
    }

    return editModel;
  }
}
